/**
 * KkdKarti.jsx — denetmen kişi sayfasının (PersonelDetayPage) Kontroller sekmesindeki "KKD" kartı.
 * Backlog #374 (backend #373), bundle #368: "Yelek verildi" anahtarının yerini alan, ÜST ÇUBUKTAKİ FUAR için madde madde
 * KKD (kişisel koruyucu donanım) onay listesi.
 *
 * Her satır bir KKD maddesi (katalogdaki HER aktif madde — fuar istemese de): simge (`ikon` bir lucide adıdır, bilinmiyorsa
 * Shirt; Baret'in simgesi bilerek yok çünkü HardHat İSG simgesi), ad, fuarın seviyesi (Engelliyor / Uyarı / İstenmiyor) ve
 * "var" anahtarı. Anahtar tıklanınca TEK madde hemen yazılır (PATCH /personnel/{id}/kkd); işaretleyen ve zaman satırın altında.
 * Fuarın istemediği (seviye 0) madde soluk basılır ama işaretlenebilir kalır. Bir işaret "kişide var" demektir (kendisinin
 * ya da bizim), "verdik" değil; KKD kart kararına HİÇ girmez.
 *
 * Üstteki durum rozeti: UYGUN yeşil · UYARI sarı (hâlâ uygun) · EKSIK kırmızı (engelleyen madde yok) · ISTENMIYOR açık gri.
 * Renkler STATUS_HEX'ten (bu temada `success` gri, `secondary` yeşil basılır; durum karelerinin hex'i tek yerde).
 *
 * Fuar (`fuarId`) yoksa kart tek satır söyler ve hiçbir şey okumaz. Okuma hatası kartın içinde; YAZMA hatası `onError`a gider
 * (okumada çağrılmaz: efekt `onError` kimliğine bağlanırsa üst bileşenin her çizimi yeniden okuma tetikler).
 *
 * @param {number}   personId
 * @param {number}   fuarId       üst çubuktaki fuar; null → "Önce üst çubuktan bir fuar seçin"
 * @param {string}   fuarAdi      başlıktaki rozet
 * @param {number}   reloadToken  değişince yeniden okunur
 * @param {boolean}  vurgu        Kişi Listesi'nin KKD karesinden gelindi: kart kırmızı çerçevelenir (#481; kimliği `belge-karti-kkd`)
 * @param {Function} onChanged   `(mesaj)` — yazma başarılı
 * @param {Function} onError      `(mesaj)` — yazma başarısız
 */
import React, { useEffect, useState } from "react";
import { Badge, Card, CardBody, CardHeader, FormGroup, Input, Spinner } from "reactstrap";
import { Shirt, Footprints, Glasses, Hand, Ear, Shield, ShieldCheck, Eye, Wind, Flame, Zap, Siren } from "lucide-react";
import { getKisiKkd, setKisiKkd } from "../../../services/kisiKkdService";
import { STATUS_HEX, NEUTRAL_GRAY } from "../../Projects/StandPills";
import { currentLocaleTag } from "../../../i18n";
import { VURGU_SINIFI } from "../kisiBaglantilari";

/**
 * Katalogdaki `ikon` (lucide adı) → bileşen. Yalnızca aşağıdakiler paketlenir (tüm lucide'ı almamak için); katalogda
 * bu listede olmayan bir ad kalırsa Shirt'e düşer. HardHat bilerek YOK: İSG simgesi, aynı satırda karışmasın.
 */
const IKONLAR = { Shirt, Footprints, Glasses, Hand, Ear, Shield, ShieldCheck, Eye, Wind, Flame, Zap, Siren };

/** Seçilebilen simge adları (#374: kalem kataloğu editörü yalnızca paketlenenleri sunar; başkası kartta Shirt'e düşerdi). */
export const KKD_IKON_ADLARI = Object.keys(IKONLAR);

/** KKD maddesinin simgesi; bilinmeyen / boş ad → Shirt. */
export function KkdIkon({ ad, ...props }) {
  const Ikon = IKONLAR[ad] ?? Shirt;
  return <Ikon {...props} />;
}

const DURUM = {
  UYGUN: { etiket: "Uygun", hex: STATUS_HEX.success },
  UYARI: { etiket: "Uyarı", hex: STATUS_HEX.warning },
  EKSIK: { etiket: "Eksik", hex: STATUS_HEX.danger },
  ISTENMIYOR: { etiket: "Bu fuarda istenmiyor", hex: null },
};

const SEVIYE = {
  2: { etiket: "Engelliyor", hex: STATUS_HEX.danger },
  1: { etiket: "Uyarı", hex: STATUS_HEX.warning },
  0: { etiket: "İstenmiyor", hex: null },
};

/**
 * Hex'li dolu rozet; hex yoksa açık gri çerçeveli. Dolu rozet `<Badge>` değil düz `span.badge`: Badge her zaman bir
 * `bg-<renk>` sınıfı basar ve o sınıf `!important` olduğundan satır içi arka plan ezilir (KartYazdirModal'daki aynı çözüm).
 */
function RenkliRozet({ hex, children, className = "", title }) {
  return hex ? (
    <span className={`badge text-white fw-normal ${className}`} style={{ background: hex }} title={title}>{children}</span>
  ) : (
    <Badge color="light" className={`text-body border fw-normal ${className}`} title={title}>{children}</Badge>
  );
}

function tarihSaat(v) {
  if (!v) return null;
  const d = new Date(String(v).replace(" ", "T"));
  return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleString(currentLocaleTag(), { dateStyle: "short", timeStyle: "short" });
}

export default function KkdKarti({ personId, fuarId, fuarAdi, reloadToken = 0, vurgu = false, onChanged, onError }) {
  const [veri, setVeri] = useState(null);
  const [okunuyor, setOkunuyor] = useState(false);
  const [okumaHatasi, setOkumaHatasi] = useState(null);
  const [isleniyor, setIsleniyor] = useState(null); // yazılan maddenin kodu

  // Fetch + setState async fonksiyonun içinde (react-hooks/set-state-in-effect: efekt gövdesinde senkron setState derleme hatası).
  useEffect(() => {
    if (personId == null || fuarId == null) return undefined;
    let iptal = false;
    async function oku() {
      setOkunuyor(true);
      setOkumaHatasi(null);
      try {
        const d = await getKisiKkd(personId, fuarId);
        if (!iptal) setVeri(d);
      } catch (e) {
        if (!iptal) setOkumaHatasi(e.message || "KKD okunamadı.");
      } finally {
        if (!iptal) setOkunuyor(false);
      }
    }
    oku();
    return () => { iptal = true; };
  }, [personId, fuarId, reloadToken]);

  // Başka bir fuarın (ya da kişinin) eski yanıtı çizilmesin.
  const guncel = veri && Number(veri.fuarId) === Number(fuarId) ? veri : null;
  const kalemler = guncel?.kalemler || [];
  const durum = DURUM[guncel?.durum] ?? null;

  async function degistir(kalem) {
    setIsleniyor(kalem.kod);
    try {
      const yeni = await setKisiKkd(personId, fuarId, [{ kod: kalem.kod, mevcut: !kalem.mevcut }]);
      if (yeni) setVeri(yeni);
      onChanged?.("KKD güncellendi.");
    } catch (e) {
      onError?.(e.message || "KKD güncellenemedi.");
    } finally {
      setIsleniyor(null);
    }
  }

  return (
    <Card id="belge-karti-kkd" className={`h-100 mb-0 ${vurgu ? VURGU_SINIFI : ""}`}>
      <CardHeader className="bg-transparent d-flex align-items-center gap-2">
        <Shirt size={16} className="text-muted" />
        <span className="fw-semibold">KKD</span>
        {fuarId != null && (
          <Badge color="light" className="text-body border fw-normal">{fuarAdi || `Fuar #${fuarId}`}</Badge>
        )}
        {(okunuyor || isleniyor) && <Spinner size="sm" color="primary" className="ms-auto" />}
      </CardHeader>
      <CardBody>
        {fuarId == null && <div className="text-muted small">Önce üst çubuktan bir fuar seçin</div>}

        {fuarId != null && okumaHatasi && <div className="text-danger small">{okumaHatasi}</div>}
        {fuarId != null && !okumaHatasi && !guncel && <div className="py-2"><Spinner size="sm" color="primary" /></div>}

        {fuarId != null && !okumaHatasi && guncel && (
          <>
            {durum && (
              <div className="mb-2">
                <RenkliRozet hex={durum.hex}>{durum.etiket}</RenkliRozet>
              </div>
            )}

            {kalemler.length === 0 && <div className="text-muted small">Tanımlı KKD maddesi yok.</div>}

            {kalemler.map((k, i) => {
              const seviye = SEVIYE[k.seviye] ?? SEVIYE[0];
              const isaretleyen = [k.isaretleyenAdi, tarihSaat(k.isaretlemeTarihi)].filter(Boolean).join(" · ");
              const id = `kkd-${personId}-${k.kod}`;
              return (
                <div
                  key={k.kod}
                  className={`d-flex align-items-center gap-2 py-2 ${i > 0 ? "border-top" : ""}`}
                  style={k.seviye === 0 ? { opacity: 0.35 } : undefined}
                >
                  <KkdIkon
                    ad={k.ikon} size={18} className="flex-shrink-0"
                    style={{ color: k.mevcut ? STATUS_HEX.success : NEUTRAL_GRAY }}
                  />
                  <div className="flex-grow-1" style={{ minWidth: 0 }}>
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                      <label htmlFor={id} className="fw-semibold mb-0">{k.ad}</label>
                      <RenkliRozet hex={seviye.hex} title={k.seviye === 0 ? "Fuar bu maddeyi istemiyor" : undefined}>
                        {seviye.etiket}
                      </RenkliRozet>
                    </div>
                    {k.isaretlendi && isaretleyen && <div className="small text-muted">{isaretleyen}</div>}
                  </div>
                  {isleniyor === k.kod && <Spinner size="sm" color="primary" />}
                  <FormGroup switch className="mb-0 flex-shrink-0">
                    <Input
                      type="switch" id={id} checked={!!k.mevcut} disabled={isleniyor != null}
                      onChange={() => degistir(k)}
                    />
                  </FormGroup>
                </div>
              );
            })}
          </>
        )}
      </CardBody>
    </Card>
  );
}
